import LaunchpadDetails from "./LaunchpadDetails"
import LaunchpadHeader from "./LaunchpadHeader"
import LaunchpadLiveSessions from "./LaunchpadLiveSessions"
import LaunchpadStageSession from "./LaunchpadStageSession"
import { Box, styled } from "@mui/material"
import LaunchpadLocation from "components/Launchpad/LaunchpadLocation"
import TicketTypesList from "components/conventions/TicketTypesList"
import useLaunchpadContext from "hooks/useLaunchpadContext"
import useRelativeTime from "hooks/useRelativeTime"
import hasTicketTypes from "lib/conventions/hasTicketTypes"
import isPaid from "lib/conventions/isPaid"
import useCurrent from "lib/use-current"
import ConventionBoolean from "models/convention-api/v1/ConventionBoolean"
import ConventionSession from "models/convention-api/v1/ConventionSession"
import Institution from "models/convention-api/v1/Institution"
import moment from "moment-timezone"
import { useState } from "react"
import { pxToRem } from "utils/helpers"

interface ILaunchpad {}

const Launchpad = (_props: ILaunchpad) => {
  // We need to make sure to store the page load time to prevent
  // Re-renders, and must be in a 5 min increment to help reduce cache misses
  const [pageLoadTime, _setPageLoadTime] = useState(() => {
    const precision = 5 * 60 * 1000
    return moment(Math.round(+new Date() / precision) * precision)
  })

  const current = useCurrent()

  const { convention } = useLaunchpadContext()

  // @ts-ignore
  const { config: providerConfig } = useCurrent()

  const { data: conventionBoolean } = ConventionBoolean.useOne({
    filter: { convention_convention_id: convention.id, setting: "stage_enabled" },
  })

  // @ts-ignore
  const { data: conventionSessionOnStage } = ConventionSession.useOne(() => {
    if (!current || !convention.id) {
      return null
    }
    return {
      filter: {
        institution_id: providerConfig.institution_id,
        convention_id: convention.id,
        start_at_lteq: pageLoadTime.clone().add(15, "minutes").toISOString(),
        end_at_gt: pageLoadTime.toISOString(),
      },
    }
  })

  // @ts-ignore
  const { data: institution } = Institution.useOne(() => {
    if (!conventionSessionOnStage) {
      return null
    }
    return {
      filter: {
        id: conventionSessionOnStage?.convention_exhibitor?.institution_id,
      },
    }
  })

  const { during } = useRelativeTime({
    // startAt: convention.start_at,
    startAt: moment(),
    endAt: convention.end_at,
  })

  const stageEnabled = Boolean(
    conventionBoolean?.value &&
    conventionSessionOnStage &&
    institution &&
    !convention.automatically_generated
  )

  return (
    <>
      <LaunchpadHeader />
      <_ContentContainer>
        <_LeftContentContainer>
          <LaunchpadDetails />
          <LaunchpadLocation />
          {stageEnabled && (
            <LaunchpadStageSession
              session={conventionSessionOnStage}
              institutionName={institution?.name}
            />
          )}
          {during && <LaunchpadLiveSessions />}
        </_LeftContentContainer>
        {isPaid(convention) && hasTicketTypes(convention) && (
          <_RightContentContainer>
            <TicketTypesList ticketTypes={convention.convention_ticket_types} />
          </_RightContentContainer>
        )}
      </_ContentContainer>
    </>
  )
}

export default Launchpad

const _ContentContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: pxToRem(40),
  flexWrap: "nowrap",
  flexDirection: "column-reverse",
  margin: `${pxToRem(40)} ${pxToRem(20)}`,

  [theme.breakpoints.up("md")]: {
    gap: pxToRem(60),
    margin: pxToRem(40),
    flexDirection: "row",
  },
}))

const _LeftContentContainer = styled(Box)(({}) => ({
  flex: 1,
}))

const _RightContentContainer = styled(Box)(({}) => ({}))
